import React, { useMemo, useState } from 'react'
import { CateringChefMenuItem, ChefMenuItem } from '@types'
import FlexContainer from '@components/common/FlexContainer'
import Button from '@components/common/form/Button'
import { AutocompleteInput } from '@containers/common/form'

type ContextType = 'customizable' | 'composed'

interface AssociateComposedMenuItemsProps {
  contextType: ContextType
  chefId: string
  chefMenuItem?: ChefMenuItem
  menuItem: CateringChefMenuItem
  searchMenuItems: (obj: Record<string, any>) => void
  confirmationModal: {
    show: (val: { text: string }) => Promise<boolean>
  }
  upsertComposedMenuItem: (
    composedItemId: string,
    chefId: string,
    customItemId: string,
  ) => void
}

const AssociateComposedMenuItems: React.FC<AssociateComposedMenuItemsProps> = ({
  chefId,
  chefMenuItem,
  contextType,
  menuItem,
  confirmationModal,
  upsertComposedMenuItem,
  searchMenuItems,
}) => {
  const [selectedItem, setSelectedItem] = useState<
    CateringChefMenuItem | undefined
  >()
  const linkSummary = useMemo(() => {
    const left =
      contextType === 'customizable' ? menuItem.name : selectedItem?.name
    const right =
      contextType === 'customizable' ? selectedItem?.name : menuItem.name
    if (!left || !right) {
      return ''
    }

    return `Link "${right}" as a composed alternative to "${left}".`
  }, [contextType, menuItem.name, selectedItem])

  const buildSearchFilters = (
    search: Record<string, any>,
  ): Record<string, any> => {
    const hasMods = contextType === 'composed' ? true : false

    return {
      ...search,
      chefIds: [chefId],
      marketTypes: ['Group Order'],
      chefStatus: '',
      isApproved: 'true',
      hasMods,
    }
  }

  const handleConfirm = async () => {
    const confirm = await confirmationModal.show({
      text: `Are you sure you want to link ${selectedItem?.name} as a composed alternative to ${menuItem.name}`,
    })
    if (confirm && selectedItem) {
      if (contextType === 'customizable' && chefMenuItem) {
        upsertComposedMenuItem(selectedItem.id, chefId, chefMenuItem.id)
      } else {
        upsertComposedMenuItem(menuItem.id, chefId, selectedItem.id)
      }
    }
  }

  const message =
    contextType === 'customizable'
      ? 'Search for a composed menu item (no mods)'
      : 'Search for a customizable menu item (has mods)'

  return (
    <FlexContainer flexDirection="column" height={'400px'}>
      <label className="text-lg font-bold text-gray-700">{message}</label>
      <div className="w-full max-w-xl pb-4">
        <AutocompleteInput
          width="45%"
          loaderFunction={(search) =>
            searchMenuItems(buildSearchFilters(search))
          }
          onSelect={(menuItem) => setSelectedItem(menuItem)}
        />
      </div>

      {selectedItem && (
        <div className="w-full max-w-xl rounded-xl border border-gray-200 bg-white p-4 shadow-md py-5">
          <div className="mb-1 text-md font-semibold text-gray-900">
            {selectedItem.name}
          </div>
          {selectedItem.description && (
            <div className="mb-2 text-md text-gray-600">
              {selectedItem.description}
            </div>
          )}
          <div className="text-md text-gray-800">{linkSummary}</div>
        </div>
      )}

      <div className="w-full flex items-center justify-center gap-3 mt-3">
        <Button
          label="Link"
          onClick={handleConfirm}
          disabled={!selectedItem}
          width={'200px'}
        />
      </div>
    </FlexContainer>
  )
}

export default AssociateComposedMenuItems
